/**
 * Cart recommendations for Dawn (Shopify).
 *
 * Shows complementary / related products in the cart drawer and on the cart page,
 * using the Shopify Product Recommendations API. Complementary products come from
 * the Shopify Search & Discovery app; related products are generated by Shopify.
 *
 * - Based on up to 3 products in the cart (most recently added first).
 * - Skips products already in the cart and products that are sold out.
 * - Preselects the variant that matches the colour of what is in the cart.
 * - "Add" refreshes the cart drawer the same way Dawn's product form does.
 */
(() => {
  if (customElements.get('cart-recommendations')) return;

  const ROOT = window.Shopify?.routes?.root || '/';
  const MAX_SOURCE_PRODUCTS = 3;
  const REFRESH_DELAY = 300;
  const cache = new Map(); // request url -> Promise<product[]>, kept for the whole page view

  const escapeHtml = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
    );

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  };

  const imageSrc = (image) => (typeof image === 'string' ? image : image?.src) || '';

  const sizedImage = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const getRecommendations = (productId, intent) => {
    const url = `${ROOT}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
    if (!cache.has(url)) {
      const request = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) => data.products || [])
        .catch(() => {
          cache.delete(url);
          return [];
        });
      cache.set(url, request);
    }
    return cache.get(url);
  };

  const intentsFor = (source) => {
    if (source === 'related') return ['related'];
    if (source === 'complementary') return ['complementary'];
    return ['complementary', 'related'];
  };

  const hasPubSub = () =>
    typeof subscribe === 'function' && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector('.cart-recs__list');
      this.status = this.querySelector('.cart-recs__status');
      this.productIds = [
        ...new Set(
          (this.dataset.productIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        ),
      ];
      this.optionValues = (this.dataset.optionValues || '')
        .split('|')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      this.limit = Math.min(Math.max(parseInt(this.dataset.limit, 10) || 3, 1), 10);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      // In the drawer Dawn re-renders this block on every cart change.
      // On the cart page it doesn't, so the block refreshes itself.
      if (this.dataset.sectionId && hasPubSub() && !this.unsubscribe) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleRefresh());
      }

      this.load();
    }

    disconnectedCallback() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      clearTimeout(this.refreshTimer);
    }

    async load() {
      if (!this.list || !this.productIds.length) return;

      const inCart = new Set(this.productIds);
      const sources = this.productIds.slice(0, MAX_SOURCE_PRODUCTS);
      const seen = new Set();
      const picked = [];

      for (const intent of intentsFor(this.dataset.source)) {
        const results = await Promise.all(sources.map((id) => getRecommendations(id, intent)));
        for (const products of results) {
          for (const product of products) {
            const id = String(product.id);
            if (inCart.has(id) || seen.has(id) || !product.available) continue;
            seen.add(id);
            picked.push(product);
          }
        }
        if (picked.length >= this.limit) break;
      }

      if (this.isConnected) this.render(picked.slice(0, this.limit));
    }

    render(products) {
      const cards = products.map((product) => this.cardHtml(product)).filter(Boolean);
      if (!cards.length) {
        this.hidden = true;
        return;
      }
      this.products = new Map(products.map((product) => [String(product.id), product]));
      this.list.innerHTML = cards.join('');
      this.hidden = false;
    }

    pickVariant(product) {
      const available = (product.variants || []).filter((variant) => variant.available);
      if (!available.length) return null;

      let best = available[0];
      let bestScore = 0;
      for (const variant of available) {
        const score = (variant.options || []).filter((option) =>
          this.optionValues.includes(String(option).trim().toLowerCase())
        ).length;
        if (score > bestScore) {
          best = variant;
          bestScore = score;
        }
      }
      return best;
    }

    optionLabel(product) {
      const names = (product.options || [])
        .map((option) => (typeof option === 'string' ? option : option?.name))
        .filter(Boolean);
      return names.length ? names.join(' / ') : 'Option';
    }

    priceHtml(variant) {
      const current = `<span class="cart-recs__price-current">${escapeHtml(formatMoney(variant.price))}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return `<s class="cart-recs__price-compare">${escapeHtml(formatMoney(variant.compare_at_price))}</s> ${current}`;
      }
      return current;
    }

    cardHtml(product) {
      const variant = this.pickVariant(product);
      if (!variant) return '';

      const url = escapeHtml(product.url);
      const image = imageSrc(variant.featured_image) || imageSrc(product.featured_image);
      const soldOut = this.dataset.soldOutLabel || 'Sold out';

      const select =
        product.variants.length > 1
          ? `<select class="cart-recs__select" aria-label="${escapeHtml(`${this.optionLabel(product)}, ${product.title}`)}">
              ${product.variants
                .map(
                  (item) =>
                    `<option value="${item.id}"${item.id === variant.id ? ' selected' : ''}${
                      item.available ? '' : ' disabled'
                    }>${escapeHtml(item.title)}${item.available ? '' : ` – ${escapeHtml(soldOut)}`}</option>`
                )
                .join('')}
            </select>`
          : '';

      return `
        <li class="cart-recs__item" data-product-id="${product.id}">
          <a class="cart-recs__media" href="${url}" tabindex="-1" aria-hidden="true">
            ${image ? `<img src="${escapeHtml(sizedImage(image, 240))}" alt="" width="120" height="120" loading="lazy">` : ''}
          </a>
          <div class="cart-recs__info">
            <a class="cart-recs__title" href="${url}">${escapeHtml(product.title)}</a>
            <div class="cart-recs__price">${this.priceHtml(variant)}</div>
            ${select}
          </div>
          <button type="button" class="cart-recs__add button button--secondary" data-variant-id="${variant.id}">
            ${escapeHtml(this.dataset.addLabel || 'Add')}
          </button>
        </li>`;
    }

    onChange(event) {
      const select = event.target.closest('.cart-recs__select');
      if (!select) return;

      const item = select.closest('.cart-recs__item');
      const product = this.products?.get(item.dataset.productId);
      const variant = product?.variants.find((entry) => String(entry.id) === select.value);
      if (!variant) return;

      item.querySelector('.cart-recs__price').innerHTML = this.priceHtml(variant);

      const button = item.querySelector('.cart-recs__add');
      button.dataset.variantId = variant.id;
      button.disabled = !variant.available;

      const image = imageSrc(variant.featured_image);
      const img = item.querySelector('.cart-recs__media img');
      if (image && img) img.src = sizedImage(image, 240);
    }

    async onClick(event) {
      const button = event.target.closest('.cart-recs__add');
      if (!button || button.disabled) return;
      event.preventDefault();

      const variantId = Number(button.dataset.variantId);
      const drawer = this.closest('cart-drawer');
      const useDrawer =
        drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';

      const body = { id: variantId, quantity: 1 };
      if (useDrawer) {
        body.sections = drawer
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',');
        body.sections_url = window.location.pathname;
      }

      this.setStatus('');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add this product to your cart.');
        }

        if (useDrawer) {
          if (hasPubSub()) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-recommendations',
              productVariantId: variantId,
              cartData: data,
            });
          }
          // Re-renders the whole drawer, including this block (the added product disappears from it).
          drawer.renderContents(data);
        } else {
          // Cart page: reload so items, totals and this block are all up to date.
          window.location.reload();
        }
      } catch (error) {
        this.setStatus(error.message);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    scheduleRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DELAY);
    }

    async refresh() {
      try {
        const cartUrl = window.routes?.cart_url || `${ROOT}cart`;
        const response = await fetch(`${cartUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('cart-recommendations');
        if (!this.isConnected) return;
        if (fresh) {
          this.replaceWith(document.importNode(fresh, true));
        } else {
          this.hidden = true; // cart is empty now
        }
      } catch (error) {
        // Keep the current list if the refresh fails.
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();